import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getPublishedProgramBySlug } from "../../api/programs";
import { Program, RequirementBlock, RequirementCategory, RequirementList } from "../../admin/types";
import { nvfoWechatQR, nvfoWhatsappQR } from "../../assets/imgExport";
import { renderRichText } from "../../utils/richText";

function imageByRole(program: Program, role: string): string | undefined {
  return program.images.find((img) => img.role === role)?.url;
}

const OPTION_LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

function isCategoryNonEmpty(category: RequirementCategory): boolean {
  if (category.kind === "list") return (category.list?.items.length ?? 0) > 0;
  return (category.options ?? []).some((o) => o.label.trim().length > 0 || o.list.items.length > 0);
}

function isRequirementBlockNonEmpty(block: RequirementBlock): boolean {
  if (block.type === "note") return block.text.trim().length > 0;
  if (block.type === "section") {
    return (block.items ?? []).some((item) => (item.type === "note" ? item.text.trim().length > 0 : isCategoryNonEmpty(item)));
  }
  return isCategoryNonEmpty(block);
}

function countCategoryItems(category: RequirementCategory): number {
  if (category.kind === "list") return category.list?.items.length ?? 0;
  return (category.options ?? []).reduce((sum, o) => sum + o.list.items.length, 0);
}

function countRequirementItems(block: RequirementBlock): number {
  if (block.type === "note") return 1;
  if (block.type === "section") {
    return (block.items ?? []).reduce((sum, item) => sum + (item.type === "note" ? 1 : countCategoryItems(item)), 0);
  }
  return countCategoryItems(block);
}

function countCategoryChars(category: RequirementCategory): number {
  if (category.kind === "list") return (category.list?.items ?? []).reduce((sum, item) => sum + item.length, 0);
  return (category.options ?? []).reduce(
    (sum, o) => sum + o.label.length + o.list.items.reduce((s, item) => s + item.length, 0),
    0,
  );
}

function countRequirementChars(block: RequirementBlock): number {
  if (block.type === "note") return block.text.length;
  if (block.type === "section") {
    return (
      block.title.length +
      (block.items ?? []).reduce((sum, item) => sum + (item.type === "note" ? item.text.length : countCategoryChars(item)), 0)
    );
  }
  return countCategoryChars(block);
}

// A category rendered as lettered routes (A/B/C) costs noticeably more
// vertical space per entry than the same content as a plain list -- each
// route is its own labeled, bordered mini-list, not just one more line. Item
// and character counts alone miss this: e.g. St Kitts and Antigua each have
// only 7-8 total requirement items (fewer than several programs that fit
// one page comfortably), but both use a 2-route investment category and
// both overflow to a second PDF page. Presence of any options-kind category
// is therefore its own signal, independent of the raw item/char thresholds.
function blockHasOptionsCategory(block: RequirementBlock): boolean {
  if (block.type === "note") return false;
  if (block.type === "section") {
    return (block.items ?? []).some((item) => item.type === "category" && item.kind === "options");
  }
  return block.kind === "options";
}

function RequirementListView({ list }: { list: RequirementList }) {
  return (
    <div className="req-list">
      {list.items.map((item, i) => (
        <div className="req-item" key={i}>
          {list.style === "numbered" && <span className="req-num">{i + 1}</span>}
          <span>{renderRichText(item)}</span>
        </div>
      ))}
    </div>
  );
}

// A category's title + body (list, or lettered routes A/B/C) -- shared by a
// top-level category and by each category nested inside a section.
function CategoryContent({ category }: { category: RequirementCategory }) {
  return (
    <div>
      <div className="req-col-title">
        <Icon icon={/主申请人|personal/i.test(category.title) ? ICONS.person : ICONS.users} size={15} />
        {renderRichText(category.title)}
      </div>
      {category.kind === "list" ? (
        <RequirementListView list={category.list ?? { style: "numbered", items: [] }} />
      ) : (
        <div className="req-options">
          {(category.options ?? []).map((option, oi) => (
            <div className="req-option" key={oi}>
              <div className="req-option-label">
                <span className="req-option-letter">{OPTION_LETTERS[oi] ?? oi + 1}</span>
                <span>{renderRichText(option.label)}</span>
              </div>
              <RequirementListView list={option.list} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// The decorative country-name watermark (.watermark in PRINT_CSS) is sized
// for short names like "VANUATU" -- a long one like "SAO TOME AND PRINCIPE"
// or "UNITED ARAB EMIRATES" at that same fixed size overflows the page width
// and bleeds into the title/heading sitting on top of it. Scales down past
// 8 characters, floored so it stays legible as a background element even
// for the longest real country names in use.
function watermarkFontSize(text: string, basePx: number): number {
  const scale = Math.min(1, 8 / text.length);
  return Math.round(basePx * Math.max(scale, 0.4));
}

// ---- Icon set — hand-drawn stroke icons matching the brand brochure design
// system built earlier this session, reused verbatim for visual consistency
// rather than pulling in an icon library just for the PDF target. Icons built
// from a head <circle> plus a body <path> (person/spouse/child) carry their
// circle here so every usage renders both parts — a bare path alone draws
// only a headless squiggle. ----
type IconDef = { d: string; circle?: { cx: number; cy: number; r: number } };
const ICONS: Record<string, IconDef> = {
  clock: { d: "M12 7v5l3 3", circle: { cx: 12, cy: 12, r: 9 } },
  calendar: { d: "M4 4h16v16H4ZM4 9h16M8 3v3M16 3v3" },
  check: { d: "M5 13l4 4L19 7" },
  building: { d: "M3 21h18M4 21V10l8-6 8 6v11M9 21v-6h6v6" },
  users: { d: "M2 20c0-3.3 3-6 7-6s7 2.7 7 6M15.5 14.2c2.7.3 5.5 2 5.5 5.8", circle: { cx: 9, cy: 8, r: 3 } },
  globe: { d: "M3 12h18M12 3c2.5 2.6 4 6 4 9s-1.5 6.4-4 9c-2.5-2.6-4-6-4-9s1.5-6.4 4-9Z", circle: { cx: 12, cy: 12, r: 9 } },
  star: { d: "M12 2.5 14.8 8.7 21.5 9.4 16.6 13.9 18 20.5 12 17.1 6 20.5 7.4 13.9 2.5 9.4 9.2 8.7Z" },
  person: { d: "M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8", circle: { cx: 12, cy: 8, r: 4 } },
  spouse: { d: "M20 21c0-3-3.5-5-8-5s-8 2-8 5", circle: { cx: 12, cy: 8, r: 4 } },
  house: { d: "M3 11 12 4l9 7M5 10v10h14V10M9 20v-6h6v6" },
  doc: { d: "M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5M9 13h6M9 17h6" },
  arrowDown: { d: "M12 4v14M6 13l6 6 6-6" },
  shield: { d: "M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" },
  phone: { d: "M6.5 3.5c1 0 2.6 2.7 2.6 3.6 0 1-1.6 1.8-1.6 2.6 0 1.6 2.7 4.3 4.3 4.3.8 0 1.6-1.6 2.6-1.6.9 0 3.6 1.6 3.6 2.6 0 1.4-1.6 3-3 3-4.5 0-11-6.5-11-11 0-1.4 1.6-3 3-3Z" },
  mail: { d: "M3 6h18v12H3ZM3 6l9 7 9-7" },
  pin: { d: "M12 21s7-6.5 7-11.5A7 7 0 1 0 5 9.5C5 14.5 12 21 12 21Z", circle: { cx: 12, cy: 9.5, r: 2.2 } },
  chat: { d: "M4 4h16v12H8l-4 4V4Z" },
};

function Icon({ icon, size = 18 }: { icon: IconDef; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7}>
      {icon.circle && <circle cx={icon.circle.cx} cy={icon.circle.cy} r={icon.circle.r} />}
      <path d={icon.d} />
    </svg>
  );
}

const ADV_ICONS = [ICONS.shield, ICONS.star, ICONS.check, ICONS.spouse, ICONS.globe, ICONS.house, ICONS.building, ICONS.doc];
const STAT_ICONS = [ICONS.building, ICONS.users, ICONS.globe, ICONS.star, ICONS.house];

// Puppeteer's navigation target for PDF export (nvfo-api's pdf.service.ts).
// Bare — no Header/Footer/Cookie, see AppRoutes.js. One <section class="a4-page">
// per screen — matches the brand's own A4-landscape brochure design system
// (dark hero, gold accents, corner frames, bento advantage grid, family-tree
// eligibility diagram) rather than a single scrolling document. Pages with no
// underlying CMS content are skipped entirely, so a sparsely-filled program
// still gets a clean short PDF instead of empty pages.
export default function ApiPrintPage() {
  const { slug } = useParams<{ slug: string }>();
  const [program, setProgram] = useState<Program | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [renderReady, setRenderReady] = useState(false);

  useEffect(() => {
    if (!slug) return;
    getPublishedProgramBySlug(slug)
      .then(setProgram)
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!program) return;
    const images = Array.from(document.querySelectorAll("#print-root img"));
    const imagesLoaded =
      images.length === 0
        ? Promise.resolve()
        : Promise.all(
            images.map(
              (img) =>
                new Promise<void>((resolve) => {
                  const el = img as HTMLImageElement;
                  if (el.complete) resolve();
                  else {
                    el.addEventListener("load", () => resolve());
                    el.addEventListener("error", () => resolve());
                  }
                }),
            ),
          );
    // Puppeteer's page.pdf() can snapshot text before the Noto Sans SC web
    // font has actually finished loading -- Chromium substitutes a fallback
    // font for whatever hasn't repainted yet, and that fallback's CJK glyphs
    // don't correspond to the same code points, producing garbled (not
    // missing) Chinese text in the exported PDF for exactly the text nodes
    // caught mid-swap, while everything else on the same page looks fine.
    // document.fonts.ready only resolves once every font face the page
    // requested has actually finished loading, closing that race.
    Promise.all([imagesLoaded, document.fonts.ready]).then(() => setRenderReady(true));
  }, [program]);

  if (notFound) return <div>program not found</div>;
  if (!program) return <div>loading…</div>;

  const programFullName = program.country + program.programTitle;
  const programTypeLabel = program.programType === "residence" ? "居留项目" : "护照项目";
  const heroImg = imageByRole(program, "hero");
  const flagImg = imageByRole(program, "flag");
  const sceneryImg = imageByRole(program, "scenery");
  const galleryImgs = program.images.filter((img) => img.role === "gallery");

  const hasOverview = (program.overviewDescription || []).length > 0 || (program.overviewStats || []).length > 0;
  const hasAdvantages = (program.advantages || []).length > 0;
  const requirementBlocks = (program.requirementGroups || []).filter(isRequirementBlockNonEmpty);
  const hasRequirements = requirementBlocks.length > 0;
  // A single bare tier with no note and no fee breakdown is just the same
  // headline number already shown on the hero page — giving it a whole
  // dedicated A4 page produces a near-empty page (see PATTERNS.md item 8).
  // Only spend a page on this section when there's real additional detail.
  const hasInvestment =
    (program.feeTables || []).length > 0 ||
    (program.investmentTiers || []).length > 1 ||
    !!program.investmentNote;
  const hasProcess = (program.processSteps || []).length > 0;

  // Each of these pages can optionally show a side photo, but a photo halves
  // the text column's width — fine for a handful of short entries, but with
  // a lot of content (long requirement paragraphs especially) it just forces
  // more wrapping and risks the page overflowing. Drop the photo once there's
  // enough content that the text needs the full page width instead, even if
  // one was uploaded.
  const eligibilityItemCount = requirementBlocks.reduce((sum, b) => sum + countRequirementItems(b), 0);
  // Item count alone misses a group like Greece's investment-tier breakdown,
  // where there are only 2-3 items but each is a full paragraph rather than
  // a short phrase -- that content overflows the narrower has-photo column
  // and spills the page onto a second sheet just as badly as too many short
  // items would. Total character count catches that case too.
  const eligibilityTotalChars = requirementBlocks.reduce((sum, b) => sum + countRequirementChars(b), 0);
  const eligibilityHasOptions = requirementBlocks.some(blockHasOptionsCategory);
  const eligibilityTooDense =
    eligibilityItemCount >= 10 ||
    requirementBlocks.length > 3 ||
    eligibilityTotalChars > 250 ||
    eligibilityHasOptions;
  // Past a certain point, dropping the photo for full width isn't enough --
  // e.g. Malta's residence/passport programs (800+ characters of policy
  // text across 4 categories) and Japan's (two whole sections of nested
  // categories + notes) still overflow at full width. This tier shrinks the
  // requirements page's own type scale/spacing (via the "very-dense" class
  // below) instead of the page just growing onto a second sheet -- scoped
  // narrowly enough that no other program's requirements page crosses it.
  const eligibilityVeryDense =
    eligibilityTooDense &&
    (eligibilityItemCount >= 12 || eligibilityTotalChars > 300 || eligibilityHasOptions);
  const advantagesTooDense = (program.advantages || []).length > 6;

  const advantagePhoto = advantagesTooDense ? undefined : imageByRole(program, "advantage");
  const eligibilityPhoto = eligibilityTooDense ? undefined : imageByRole(program, "eligibility");
  // Unlike advantages/eligibility, the process photo is never dropped for
  // being "too dense" -- .step-card is compact enough (see PRINT_CSS) that
  // even the longest real process (12 steps, Portugal) fits the narrower
  // with-photo 3-column grid, so a process page always gets its side photo
  // when one is uploaded.
  const processPhoto = imageByRole(program, "process");

  return (
    <div id="print-root">
      <style>{PRINT_CSS}</style>

      {/* ============ PAGE 1 · HERO ============ */}
      <section className="hero a4-page">
        <div className="texture" />
        {heroImg && <img className="hero-bg" src={heroImg} alt="" />}
        <div className="hero-scrim" />
        <div className="corner-frame">
          <span className="tl1" /><span className="tl2" /><span className="br1" /><span className="br2" />
        </div>
        <div className="inner">
          <div className="hero-grid">
            <div>
              <span className="pill">
                {flagImg && <img className="flag-img" src={flagImg} alt="" />}
                {program.heroBadge || programTypeLabel}
              </span>
              <h1>
                <span>{program.country}</span>
                <span className="type">{program.programTitle}</span>
              </h1>
              <div className="gold-divider on-dark"><span className="line" /><span className="diamond" /><span className="line" /></div>
              <div className="stats">
                {program.investmentAmount && (
                  <span className="chip"><Icon icon={ICONS.clock} size={15} />{program.investmentAmount}</span>
                )}
                {program.durationLabel && (
                  <span className="chip"><Icon icon={ICONS.calendar} size={15} />{program.durationLabel}</span>
                )}
              </div>
              {program.heroDescription && <p className="desc">{program.heroDescription}</p>}
            </div>
            {(program.heroHighlights || []).length > 0 && (
              <div className="hero-card">
                <span className="tag"><span />项目速览</span>
                <div className="hero-card-title">为什么选择<br />{program.country}？</div>
                <ul>
                  {program.heroHighlights!.map((h, i) => (
                    <li key={i}><Icon icon={ICONS.check} size={16} />{h}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {(program.trustStats || []).length > 0 && (
            <div
              className="hero-trust"
              style={{ gridTemplateColumns: `repeat(${Math.min(program.trustStats!.length, 4)},1fr)` }}
            >
              {program.trustStats!.slice(0, 4).map((s, i) => (
                <div className="item" key={i}><p className="v">{s.value}</p><p className="l">{s.label}</p></div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ============ OVERVIEW ============ */}
      {hasOverview && (
        <section className="overview a4-page">
          {sceneryImg && <img className="overview-bg" src={sceneryImg} alt="" />}
          <div className="overview-scrim" />
          {!sceneryImg && (
            <svg className="overview-fallback" viewBox="0 0 300 260" fill="none">
              <path d="M40 200c10-40 20-80 50-110c25-25 55-35 90-30c10 22-5 35-25 40c20 5 32 22 25 40c-8 20-30 22-45 15c5 20-8 38-30 40c-25 2-45-15-42-40c-10 20-25 30-23 45Z" stroke="currentColor" strokeWidth="1.4" />
            </svg>
          )}
          <span className="watermark" style={{ fontSize: watermarkFontSize(program.countryEn, 130) }}>
            {program.countryEn.toUpperCase()}
          </span>
          <div className="corner-frame">
            <span className="tl1" /><span className="tl2" /><span className="br1" /><span className="br2" />
          </div>
          <div className="container">
            <div className="overview-top">
              <div className="eyebrow"><span className="line" /><span>国家概况 · Country Overview</span></div>
              <div className="country-name">
                {flagImg && <img className="flag-img-lg" src={flagImg} alt="" />}
                <h2>{program.country}</h2>
              </div>
              <div className="gold-divider"><span className="line" /><span className="diamond" /><span className="line" /></div>
              <div className="desc">
                {(program.overviewDescription || []).map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>
            {(program.overviewStats || []).length > 0 && (
              <div className="stat-list">
                {program.overviewStats!.map((s, i) => (
                  <div className="stat-item" key={i}>
                    <span className="stat-icon"><Icon icon={STAT_ICONS[i % STAT_ICONS.length]} size={16} /></span>
                    <div><div className="label">{s.label}</div><div className="value">{s.value}</div></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ============ SCENERY (only when gallery images exist) ============ */}
      {galleryImgs.length > 0 && (
        <section className="scenery a4-page">
          <div className="container">
            <div className="head">
              <div className="eyebrow"><span className="line" /><span>风光掠影 · Scenery</span></div>
              <h2 className="section-title">{program.country}</h2>
            </div>
            <div className="tiles">
              {galleryImgs.slice(0, 4).map((img) => (
                <div className="tile" key={img.id}>
                  <img src={img.url} alt={img.caption || ""} />
                  {img.caption && (
                    <span className="cap"><b>{img.caption}</b>{img.captionEn || ""}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ ADVANTAGES ============ */}
      {hasAdvantages && (
        <section className="advantages a4-page">
          <span className="watermark" style={{ fontSize: watermarkFontSize(program.countryEn, 110) }}>
            {program.countryEn.toUpperCase()}
          </span>
          <div className="container">
            <div className={`adv-layout ${advantagePhoto ? "has-photo" : ""}`}>
              {advantagePhoto && (
                <div className="adv-photo"><img src={advantagePhoto} alt="" /></div>
              )}
              <div>
                <div className="eyebrow"><span className="line" /><span>项目优势 · Why This Program</span></div>
                <h2 className="section-title">{programFullName}有什么优势？</h2>
                <div className={`bento ${advantagePhoto ? "with-photo" : ""}`}>
                  {program.advantages!.map((a, i) => (
                    <div className={`adv-card ${i === 0 ? "feature" : ""}`} key={i}>
                      <div className="top-row">
                        <div className="adv-icon"><Icon icon={ADV_ICONS[i % ADV_ICONS.length]} size={18} /></div>
                        <span className="num">{String(i + 1).padStart(2, "0")}</span>
                      </div>
                      <div className="adv-title">{a.title}</div>
                      <p>{a.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ REQUIREMENTS ============ */}
      {hasRequirements && (
        <section className={`requirements a4-page ${eligibilityVeryDense ? "very-dense" : ""}`}>
          <div className="container">
            <div className={`grid ${eligibilityPhoto ? "has-photo" : ""}`}>
              <div className="req-content">
                <div className="req-heading">
                  <div className="eyebrow"><span className="line" /><span>申请条件 · Eligibility</span></div>
                  <h2>谁可以申请{programFullName}？</h2>
                  <p className="sub">主申请人及其合格家庭成员均可作为附属申请人一并递交申请，实现全家身份规划。</p>
                </div>
                <div className="req-groups">
                  {requirementBlocks.map((block, gi) => {
                    if (block.type === "note") {
                      return <p className="req-note" key={gi}>{renderRichText(block.text)}</p>;
                    }
                    if (block.type === "section") {
                      return (
                        <div className="req-section" key={gi}>
                          <div className="req-section-title">{renderRichText(block.title)}</div>
                          <div className="req-section-categories">
                            {(block.items ?? []).map((item, ci) =>
                              item.type === "note" ? (
                                <p className="req-note" key={ci}>{renderRichText(item.text)}</p>
                              ) : (
                                <CategoryContent category={item} key={ci} />
                              ),
                            )}
                          </div>
                        </div>
                      );
                    }
                    return <CategoryContent category={block} key={gi} />;
                  })}
                </div>
              </div>
              {eligibilityPhoto && (
                <div className="req-photo"><img src={eligibilityPhoto} alt="" /></div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ============ INVESTMENT ============ */}
      {hasInvestment && (
        <section className="investment a4-page">
          <div className="container">
            <div className="eyebrow"><span className="line" /><span>投资选项 · Investment Options</span></div>
            <h2 className="section-title" style={{ marginBottom: 26 }}>{program.investmentTitle || "投资方式与费用明细"}</h2>
            <div className="grid">
              {(program.investmentTiers || []).length > 0 && (
                <div className="invest-box">
                  {program.investmentNote && <p className="note" style={{ marginTop: 0 }}>{program.investmentNote}</p>}
                  <div className="amount">
                    {program.investmentTiers![0].amount}
                    <span>{program.investmentTiers![0].label}</span>
                  </div>
                  {program.investmentTiers!.length > 1 && (
                    <ul>
                      {program.investmentTiers!.slice(1).map((t, i) => (
                        <li key={i}>{t.label} <b>{t.amount}</b></li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {(program.feeTables || []).length > 0 && (
                <div>
                  {program.feeTables!.map((table, i) => (
                    <div className="fee-table" key={i}>
                      <div className="head"><span className="icon"><Icon icon={ICONS.doc} size={14} /></span>{table.title}</div>
                      <table>
                        <tbody>
                          <tr><th>项目</th><th>标准</th></tr>
                          {table.rows.map((row, j) => (
                            <tr key={j}><td>{row.label}</td><td>{row.amount}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ============ PROCESS ============ */}
      {hasProcess && (
        <section className="process a4-page">
          <div className="container process-head">
            <div className="eyebrow"><span className="line" /><span>申请流程 · Process</span></div>
            <h2 className="section-title">{programFullName}申请流程</h2>
            {program.processDurationLabel && (
              <div className="sub">总周期约 <span className="badge">{program.processDurationLabel}</span></div>
            )}
          </div>
          <div className="container flow-container">
            <div className={`process-layout ${processPhoto ? "has-photo" : ""}`}>
              {processPhoto && (
                <div className="process-photo"><img src={processPhoto} alt="" /></div>
              )}
              <div className={`steps-grid ${processPhoto ? "with-photo" : ""}`}>
                {program.processSteps!.map((step, i) => (
                  <div className="step-card" key={i}>
                    <span className="num">{String(i + 1).padStart(2, "0")}</span>
                    <h4>{step.title}</h4>
                    <p>{step.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ CTA + FOOTER ============ */}
      <section className="cta a4-page">
        <div className="glow" />
        <div className="corner-frame" style={{ inset: 20 }}>
          <span className="tl1" /><span className="tl2" /><span className="br1" /><span className="br2" />
        </div>
        <div className="inner">
          <div className="cta-title">{program.ctaTitle || `对${programFullName}感兴趣？`}</div>
          <div className="gold-divider on-dark center"><span className="line" /><span className="diamond" /><span className="line" /></div>
          <p>{program.ctaDescription || "联系新愿景家族办公室，获取专属方案与一对一咨询。"}</p>
          <div className="contact-block">
            <div className="qr-item">
              <img className="qr-invert" src={nvfoWechatQR} alt="微信" />
              <span>微信扫码咨询</span>
            </div>
            <div className="qr-item">
              <img className="qr-invert" src={nvfoWhatsappQR} alt="WhatsApp" />
              <span>WhatsApp 咨询</span>
            </div>
            <div className="contact-details">
              <div className="row"><Icon icon={ICONS.phone} size={14} /><span>+44 (0) 20 3907 4025</span></div>
              <div className="row"><Icon icon={ICONS.mail} size={14} /><span>fo@newvgroup.com</span></div>
              <div className="row"><Icon icon={ICONS.pin} size={14} /><span>16 Berkeley Street, Mayfair, London, W1J 8DZ, United Kingdom</span></div>
            </div>
          </div>
        </div>
        <div className="page-footer">
          <span className="wordmark">NEW VISION FAMILY OFFICE</span>
          <div className="fine">
            本页面内容根据公开资料与项目官方文件整理，仅供参考，具体条款以政府最新政策与官方文件为准。如需进一步咨询，请联系 fo@newvgroup.com
          </div>
        </div>
      </section>

      {renderReady && <div id="pdf-ready" />}
    </div>
  );
}

const PRINT_CSS = `
  :root{
    --main:#3B4B5F; --secondary-dark:#232d39; --secondary-light:#89939f;
    --fourth-light:#d8dbdf; --c-gray:#f8f8f5; --gold:#b89355; --gold-light:#e0ca9d;
    --serif:'Cormorant Garamond','Times New Roman',Times,serif;
    --sans:'Noto Sans SC','PingFang SC','Microsoft YaHei',-apple-system,sans-serif;
  }
  #print-root{font-family:var(--sans);color:var(--secondary-dark);}
  #print-root *{box-sizing:border-box;}
  #print-root img{max-width:100%;display:block;}
  #print-root h1,#print-root h2,#print-root h3,#print-root p,#print-root ul{margin:0;}
  #print-root ul{padding:0;list-style:none;}
  /* min-height (not a fixed height) + visible (not hidden) overflow: a page
     with normal content still comes out exactly 210mm tall, but a page with
     unusually dense content (e.g. a long eligibility list) grows instead of
     silently clipping — Chromium's print engine then just spills the extra
     content onto a following physical page rather than losing it. */
  .a4-page{width:297mm;min-height:210mm;overflow:visible;position:relative;page-break-after:always;break-after:page;}
  .a4-page:last-of-type{page-break-after:auto;}
  .container{max-width:1240px;margin:0 auto;padding:0 60px;}

  .corner-frame{position:absolute;inset:14px;pointer-events:none;z-index:5;}
  .corner-frame span{position:absolute;width:40px;height:40px;border-color:var(--gold);}
  .corner-frame .tl1{top:0;left:0;border-top:1px solid var(--gold);border-left:1px solid var(--gold);}
  .corner-frame .tl2{top:6px;left:6px;width:28px;height:28px;border-top:1px solid rgba(184,147,85,.5);border-left:1px solid rgba(184,147,85,.5);}
  .corner-frame .br1{bottom:0;right:0;border-bottom:1px solid var(--gold);border-right:1px solid var(--gold);}
  .corner-frame .br2{bottom:6px;right:6px;width:28px;height:28px;border-bottom:1px solid rgba(184,147,85,.5);border-right:1px solid rgba(184,147,85,.5);}

  .gold-divider{display:flex;align-items:center;gap:12px;margin:18px 0;}
  .gold-divider.center{justify-content:center;}
  .gold-divider span.line{height:1px;width:40px;background:var(--gold);}
  .gold-divider span.diamond{width:6px;height:6px;background:var(--gold);transform:rotate(45deg);flex-shrink:0;}
  .gold-divider.on-dark span.line,.gold-divider.on-dark span.diamond{background:rgba(184,147,85,.7);}

  .eyebrow{display:flex;align-items:center;gap:16px;margin-bottom:10px;}
  .eyebrow .line{height:1px;width:40px;background:var(--gold);flex-shrink:0;}
  .eyebrow span{font-size:12px;letter-spacing:.24em;text-transform:uppercase;color:rgba(59,75,95,.6);}
  h2.section-title{font-size:26px;font-weight:700;letter-spacing:.02em;color:var(--secondary-dark);}

  .pill{display:inline-flex;align-items:center;gap:8px;font-size:13px;letter-spacing:.05em;border:1px solid rgba(184,147,85,.4);border-radius:999px;padding:7px 15px;color:var(--gold-light);text-transform:uppercase;}
  .pill.on-dark{margin-bottom:14px;}
  .flag-img{width:16px;height:11px;border-radius:2px;object-fit:cover;}
  .flag-img-lg{height:26px;border-radius:3px;object-fit:cover;border:1px solid var(--fourth-light);}

  /* Hero */
  .hero{display:flex;flex-direction:column;justify-content:center;background:linear-gradient(115deg,var(--secondary-dark) 0%,#2c3948 45%,var(--main) 100%);color:#fff;}
  .hero .texture{position:absolute;inset:0;z-index:1;opacity:.5;background-image:radial-gradient(circle, rgba(255,255,255,.08) 1px, transparent 1px);background-size:26px 26px;}
  .hero-bg{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;z-index:0;}
  .hero-scrim{position:absolute;inset:0;z-index:2;background:linear-gradient(90deg, rgba(35,45,57,.92) 0%, rgba(35,45,57,.75) 45%, rgba(35,45,57,.35) 100%);}
  .hero .inner{position:relative;z-index:10;padding:0 60px;max-width:1122px;width:100%;margin:0 auto;}
  .hero-grid{display:grid;grid-template-columns:1.15fr .85fr;gap:40px;align-items:center;}
  .hero h1{font-size:52px;font-weight:700;letter-spacing:.02em;line-height:1.15;display:flex;flex-wrap:wrap;align-items:baseline;gap:12px;}
  .hero h1 .type{font-family:var(--serif);font-style:italic;font-weight:500;color:var(--gold-light);font-size:.6em;}
  .hero .stats {display:flex;flex-wrap:wrap;gap:12px;margin:18px 0 20px;font-size:16px;font-weight:600;background:none;}
  .hero .stats .chip{display:flex;align-items:center;gap:16px;background:rgba(255,255,255,.94);border:1px solid rgba(255,255,255,.94);border-radius:999px;padding:8px 16px;color:var(--secondary-dark);font-weight:700;}
  .hero p.desc{opacity:.82;max-width:540px;line-height:1.75;font-size:16px;}

  .hero-card{background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.18);border-radius:18px;padding:22px;}
  .hero-card .tag{display:inline-flex;align-items:center;gap:8px;font-size:10.5px;letter-spacing:.18em;text-transform:uppercase;color:rgba(255,255,255,.75);border:1px solid rgba(255,255,255,.3);border-radius:999px;padding:5px 13px;margin-bottom:13px;}
  .hero-card .tag span{width:6px;height:6px;border-radius:999px;background:var(--gold-light);}
  .hero-card-title{color:#fff;font-size:20px;font-weight:600;margin-bottom:16px;line-height:1.4;}
  .hero-card ul{display:flex;flex-direction:column;gap:10px;}
  .hero-card li{display:flex;align-items:center;gap:10px;font-size:14.5px;color:rgba(255,255,255,.85);}
  .hero-card li svg{color:var(--gold-light);flex-shrink:0;}

  .hero-trust{margin-top:60px;padding-top:20px;border-top:1px solid rgba(255,255,255,.15);display:grid;grid-template-columns:repeat(4,1fr);gap:38px;}
  .hero-trust .item{border-left:2px solid var(--gold);padding-left:16px;display:flex;flex-direction: column;gap:12px;}
  .hero-trust .v{font-family:var(--serif);font-size:18px;font-weight:600;color:#fff;line-height:1.5;}
  .hero-trust .l{font-size:12px;letter-spacing:.02em;color:rgba(255,255,255,.6);line-height:1.5;}

  /* Overview — scenery image fills the whole page as a background, with a
     light scrim fading from opaque (text side) to transparent (image side)
     so the map/photo stays visible while text stays legible. */
  .overview{background:var(--c-gray);position:relative;display:flex;align-items:stretch;}
  .overview-bg{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;z-index:0;}
  .overview-scrim{position:absolute;inset:0;z-index:1;background:linear-gradient(90deg, rgba(248,248,245,.98) 0%, rgba(248,248,245,.93) 40%, rgba(248,248,245,.55) 68%, rgba(248,248,245,.18) 100%);}
  .overview-fallback{position:absolute;right:8%;top:50%;transform:translateY(-50%);width:34%;height:auto;color:var(--gold);opacity:.15;z-index:0;}
  .overview .watermark{position:absolute;bottom:-30px;left:60px;font-family:var(--serif);font-weight:600;font-style:italic;font-size:130px;color:rgba(184,147,85,.06);white-space:nowrap;z-index:0;letter-spacing:.01em;}
  .overview .container{position:relative;z-index:2;width:100%;max-width:660px;margin:0;padding:64px 60px;display:flex;flex-direction:column;justify-content:space-between;}
  .overview .country-name{display:flex;align-items:center;gap:16px;margin-bottom:0;}
  .overview .country-name h2{font-size:36px;font-weight:700;}
  .overview .flag-img-lg{height:32px;}
  /* #print-root h1,h2,h3,p,ul{margin:0} above is an ID selector, which beats
     this plain class rule on specificity regardless of source order (same
     issue noted on .qr-item img below) -- repeat the #print-root prefix so
     paragraphs stay visually separated when there's more than one. */
  #print-root .overview .desc p{color:rgba(35,45,57,.8);margin-bottom:14px;font-size:15.5px;line-height:1.8;}
  /* .container is flex-column + space-between (see above), so .stat-list
     sits pinned to the bottom of the page rather than right under the
     description -- no margin-top needed here to force that gap anymore. */
  .overview .stat-list{display:flex;flex-direction:column;gap:14px;}
  .overview .stat-item{display:flex;align-items:center;gap:14px;background:#fff;border:1px solid var(--fourth-light);border-radius:14px;padding:14px 16px;}
  .overview .stat-icon{width:42px;height:42px;border-radius:999px;flex-shrink:0;background:rgba(184,147,85,.12);border:1px solid rgba(184,147,85,.3);display:flex;align-items:center;justify-content:center;color:var(--gold);}
  .overview .stat-item .label{font-size:12px;color:var(--secondary-light);}
  .overview .stat-item .value{font-weight:600;color:var(--secondary-dark);font-size:14.5px;}

  /* Scenery */
  .scenery{background:var(--c-gray);display:flex;align-items:center;}
  .scenery .container{width:100%;}
  .scenery .head{margin-bottom:32px;}
  .scenery .tiles{display:grid;grid-template-columns:repeat(4,1fr);gap:22px;}
  .scenery .tile{position:relative;aspect-ratio:4/3;border-radius:14px;overflow:hidden;border:1px solid var(--fourth-light);}
  .scenery .tile img{width:100%;height:100%;object-fit:cover;}
  .scenery .tile .cap{position:absolute;left:0;right:0;bottom:0;padding:10px 12px;font-size:11px;color:#fff;background:linear-gradient(0deg, rgba(0,0,0,.6), transparent);}
  .scenery .tile .cap b{display:block;font-weight:600;font-size:12.5px;}

  /* Advantages */
  .advantages{position:relative;overflow:hidden;background:#fff;display:flex;align-items:flex-start;}
  .advantages .watermark{position:absolute;top:0;left:50%;transform:translateX(-50%);font-family:var(--serif);font-weight:600;font-style:italic;font-size:110px;color:rgba(184,147,85,.07);white-space:nowrap;z-index:0;letter-spacing:.01em;}
  .advantages .container{position:relative;z-index:1;width:100%;padding-top:64px;padding-bottom:50px;}
  /* A uniform (non-spanning) grid — Chromium's print engine (page.pdf()) has
     a real bug where text inside CSS Grid cells that use explicit spanning
     (grid-row/grid-column ranges, as the bento "feature card" layout did)
     silently fails to paint, even though it renders fine on-screen. Verified
     directly: the .requirements grid (plain, no spanning) printed correctly
     while this one didn't, and the DOM/live-browser content were confirmed
     correct in both cases — so no spanning anywhere in this grid. */
  .adv-layout{display:grid;grid-template-columns:1fr;column-gap:44px;align-items:stretch;}
  .adv-layout.has-photo{grid-template-columns:1fr 1.5fr;}
  .adv-photo{border-radius:20px;overflow:hidden;min-height:100%;}
  .adv-photo img{width:100%;height:100%;object-fit:cover;}
  .bento{display:grid;grid-template-columns:repeat(4,1fr);gap:13px;margin-top:22px;}
  .bento.with-photo{grid-template-columns:repeat(2,1fr);}
  .adv-card{position:relative;display:flex;flex-direction:column;gap:8px;background:var(--c-gray);border-radius:14px;padding:16px 18px;}
  .adv-card:nth-child(even){background:#fff;border:1px solid var(--fourth-light);}
  .adv-card .top-row{display:flex;align-items:center;justify-content:space-between;gap:8px;}
  /* Flat fill, not a gradient — Chromium's print engine leaves a faint seam
     along diagonal linear-gradient backgrounds inside a rounded/clipped box
     at this size, visible as a stray line across the icon tile. */
  .adv-icon{width:36px;height:36px;border-radius:10px;flex-shrink:0;background:rgba(184,147,85,.16);border:1px solid rgba(184,147,85,.35);color:var(--gold);display:flex;align-items:center;justify-content:center;}
  .adv-card .num{font-family:var(--serif);font-size:21px;font-weight:600;color:rgba(184,147,85,.45);}
  .adv-title{font-size:14.5px;font-weight:700;line-height:1.4;min-height:1.4em;}
  .adv-card p{font-size:12px;color:rgba(35,45,57,.68);line-height:1.55;}
  /* Flat fill, not a diagonal gradient — same Chromium print-engine seam
     noted on .adv-icon above, just at card scale instead of icon scale. */
  .adv-card.feature{background:rgba(184,147,85,.1);border:1px solid rgba(184,147,85,.3);}

  /* Requirements — content (heading + all requirement groups) stays on the
     left; the country photo, when present, takes the right column. */
  .requirements{background:var(--c-gray);display:flex;align-items:flex-start;}
  .requirements .container{width:100%;padding-top:64px;padding-bottom:50px;}
  .requirements .grid{display:grid;grid-template-columns:1fr;gap:56px;height:100%;}
  .requirements .grid.has-photo{grid-template-columns:1.5fr 1fr;}
  .req-photo{border-radius:20px;overflow:hidden;min-height:100%;}
  .req-photo img{width:100%;height:100%;object-fit:cover;}
  .req-heading h2{font-size:32px;font-weight:700;line-height:1.35;margin-top:10px;max-width:640px;}
  .req-heading p.sub{color:var(--secondary-light);margin-top:14px;max-width:520px;line-height:1.8;font-size:15px;}
  .req-groups{display:grid;grid-template-columns:1fr 1fr;column-gap:36px;margin-top:30px;}
  .req-col-title{display:flex;align-items:center;gap:10px;font-size:14px;font-weight:600;letter-spacing:.04em;color:var(--main);text-transform:uppercase;margin-bottom:13px;margin-top:22px;}
  .req-groups>div:nth-child(1) .req-col-title,.req-groups>div:nth-child(2) .req-col-title{margin-top:0;}
  .req-col-title svg{color:var(--gold);}
  .req-list{display:flex;flex-direction:column;gap:9px;}
  .req-item{display:flex;align-items:flex-start;gap:12px;background:#fff;border:1px solid var(--fourth-light);border-radius:12px;padding:11px 16px;font-size:13.5px;line-height:1.5;color:rgba(35,45,57,.85);}
  .req-num{width:23px;height:23px;border-radius:999px;flex-shrink:0;background:rgba(184,147,85,.12);border:1px solid rgba(184,147,85,.4);color:var(--gold);display:flex;align-items:center;justify-content:center;font-size:11.5px;font-weight:700;font-family:var(--serif);}
  /* A standalone note block (no category title) spans both req-groups
     columns instead of squeezing into a single 1fr cell. */
  .req-note{grid-column:1 / -1;font-size:13px;font-style:italic;color:var(--secondary-light);line-height:1.7;}
  /* A "大分类" section wraps several categories under one umbrella heading
     -- spans the full width like a note, then lays its own categories out
     in the same 2-col rhythm as the top-level req-groups grid. */
  .req-section{grid-column:1 / -1;background:rgba(184,147,85,.07);border:1px solid rgba(184,147,85,.25);border-radius:16px;padding:20px 24px;}
  .req-section-title{font-size:15px;font-weight:700;color:var(--secondary-dark);text-transform:uppercase;letter-spacing:.03em;margin-bottom:16px;padding-bottom:8px;border-bottom:1px solid rgba(184,147,85,.3);}
  .req-section-categories{display:grid;grid-template-columns:1fr 1fr;column-gap:36px;row-gap:22px;}
  .req-section-categories .req-col-title{margin-top:0;}
  /* Routes A/B/C within an "options" category -- each is its own labeled
     mini-list rather than one flat list, so a category like an investment
     breakdown with genuinely different paths reads as distinct options. */
  .req-options{display:flex;flex-direction:column;gap:16px;}
  .req-option-label{display:flex;align-items:center;gap:9px;font-size:13.5px;font-weight:600;color:var(--main);margin-bottom:8px;}
  .req-option-letter{width:22px;height:22px;border-radius:999px;flex-shrink:0;background:rgba(184,147,85,.14);border:1px solid rgba(184,147,85,.4);color:var(--gold);display:flex;align-items:center;justify-content:center;font-family:var(--serif);font-weight:700;font-size:12px;}
  /* "very-dense" -- an exceptionally content-heavy eligibility page (e.g.
     Malta's 800+ characters of policy text, Japan's two full sections of
     nested categories/notes) still overflows a page even at full width with
     the defaults above. Tightens this page's own type scale and spacing
     instead of letting it grow onto a second sheet -- scoped to this one
     class so it never touches any other program's requirements page. */
  .requirements.very-dense .container{padding-top:38px;padding-bottom:26px;}
  .requirements.very-dense .req-heading h2{font-size:24px;margin-top:6px;}
  .requirements.very-dense .req-heading p.sub{font-size:12.5px;margin-top:7px;line-height:1.55;}
  .requirements.very-dense .req-groups{column-gap:28px;margin-top:16px;}
  .requirements.very-dense .req-col-title{font-size:12px;margin-bottom:7px;margin-top:14px;}
  .requirements.very-dense .req-groups>div:nth-child(1) .req-col-title,
  .requirements.very-dense .req-groups>div:nth-child(2) .req-col-title{margin-top:0;}
  .requirements.very-dense .req-list{gap:5px;}
  .requirements.very-dense .req-item{padding:7px 11px;font-size:11.5px;line-height:1.35;gap:8px;}
  .requirements.very-dense .req-num{width:17px;height:17px;font-size:9.5px;}
  .requirements.very-dense .req-note{font-size:11px;line-height:1.4;}
  .requirements.very-dense .req-section{padding:11px 16px;}
  .requirements.very-dense .req-section-title{font-size:12px;margin-bottom:8px;padding-bottom:4px;}
  .requirements.very-dense .req-section-categories{column-gap:28px;row-gap:12px;}
  .requirements.very-dense .req-options{gap:8px;}
  .requirements.very-dense .req-option-label{font-size:11.5px;margin-bottom:4px;}
  .requirements.very-dense .req-option-letter{width:17px;height:17px;font-size:9.5px;}

  /* Investment */
  .investment{background:#fff;display:flex;align-items:center;}
  .investment .container{width:100%;}
  .investment .grid{display:grid;grid-template-columns:.9fr 1.1fr;gap:48px;}
  .invest-box{position:relative;overflow:hidden;border:1px solid var(--fourth-light);border-radius:16px;padding:24px;background:linear-gradient(160deg,#fff,#faf8f4);}
  .invest-box .amount{font-family:var(--serif);font-size:38px;font-weight:600;color:var(--main);line-height:1;}
  .invest-box .amount span{font-size:14.5px;font-family:var(--sans);font-weight:500;color:var(--secondary-light);margin-left:8px;}
  .invest-box p.note{color:var(--secondary-light);font-size:12.5px;margin-bottom:9px;line-height:1.6;}
  .invest-box ul{margin-top:16px;display:flex;flex-direction:column;gap:8px;}
  .invest-box li{display:flex;justify-content:space-between;gap:12px;font-size:13px;border-top:1px solid var(--fourth-light);padding-top:8px;}
  .invest-box li b{color:var(--secondary-dark);}
  .fee-table{border:1px solid var(--fourth-light);border-radius:12px;overflow:hidden;margin-bottom:14px;background:#fff;}
  .fee-table:last-child{margin-bottom:0;}
  .fee-table .head{display:flex;align-items:center;gap:10px;background:linear-gradient(135deg,rgba(184,147,85,.16),rgba(224,202,157,.08));color:var(--secondary-dark);font-size:13px;font-weight:700;letter-spacing:.02em;padding:10px 16px;border-bottom:1px solid rgba(184,147,85,.3);}
  .fee-table .head .icon{width:26px;height:26px;border-radius:999px;flex-shrink:0;background:#fff;border:1px solid rgba(184,147,85,.4);color:var(--gold);display:flex;align-items:center;justify-content:center;}
  .fee-table table{width:100%;border-collapse:collapse;font-size:12.5px;}
  .fee-table th{text-align:left;font-weight:500;color:var(--secondary-light);padding:8px 16px;border-bottom:1px solid var(--fourth-light);font-size:10.5px;text-transform:uppercase;letter-spacing:.04em;}
  .fee-table td{padding:8px 16px;border-bottom:1px solid var(--fourth-light);}
  .fee-table tbody tr:nth-child(even) td{background:rgba(184,147,85,.04);}
  .fee-table tr:last-child td{border-bottom:none;}
  .fee-table td:last-child,.fee-table th:last-child{text-align:right;font-weight:600;color:var(--secondary-dark);}

  /* Process */
  .process{background:white;display:flex;flex-direction:column;}
  .process .process-head{padding-top:64px;padding-bottom:0;}
  .process .flow-container{flex:1;display:flex;flex-direction:column;justify-content:center;width:100%;padding-top:36px;padding-bottom:50px;}
  .process .section-title.on-dark{color:#fff;}
  .process .sub{display:flex;align-items:center;gap:14px;color:var(--secondary-light);font-size:15px;margin-top:10px;}
  .process .sub.on-dark{color:rgba(255,255,255,.75);}
  .process .sub .badge{background:rgba(184,147,85,.12);color:var(--gold);border:1px solid rgba(184,147,85,.3);border-radius:999px;padding:5px 16px;font-weight:600;font-size:13.5px;}
  /* Step grid — matches the site's own program-page process cards (plain
     numbered cards, no connecting timeline) rather than the earlier
     circle+dashed-line flow design. */
  .process-layout{display:grid;grid-template-columns:1fr;gap:28px;align-items:stretch;}
  .process-layout.has-photo{grid-template-columns:.9fr 2fr;}
  .process-photo{border-radius:20px;overflow:hidden;min-height:100%;}
  .process-photo img{width:100%;height:100%;object-fit:cover;}
  .steps-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;}
  /* 3 columns (not 2) — with each block shrunk below, this still comfortably
     fits the longest real process (12 steps, Portugal) in 4 rows alongside
     the photo, instead of forcing the photo to be dropped for denser ones. */
  .steps-grid.with-photo{grid-template-columns:repeat(3,1fr);}
  .step-card{background:var(--c-gray);border-radius:10px;padding:9px 11px;display:flex;flex-direction:column;gap:3px;}
  .step-card:nth-child(even){background:#fff;border:1px solid var(--fourth-light);}
  .step-card .num{font-family:var(--serif);font-size:16px;font-weight:600;color:rgba(184,147,85,.5);}
  .step-card h4{font-size:12px;font-weight:700;color:var(--secondary-dark);line-height:1.2;}
  .step-card p{font-size:10px;color:var(--secondary-light);line-height:1.35;}

  /* CTA */
  .cta{background:var(--secondary-dark);text-align:center;display:flex;align-items:center;justify-content:center;}
  .cta .glow{position:absolute;inset:0;background:radial-gradient(ellipse 700px 400px at 50% 0%, rgba(184,147,85,.18), transparent 70%);}
  .cta .inner{position:relative;z-index:5;max-width:620px;margin:0 auto;}
  .cta-title{color:#fff;font-size:28px;font-weight:700;margin-top:6px;}
  .cta p{color:rgba(255,255,255,.6);line-height:1.8;margin:0 auto;font-size:14px;}
  .contact-block{margin-top:32px;display:inline-flex;align-items:center;gap:36px;text-align:left;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.15);border-radius:18px;padding:22px 32px;}
  .qr-item{display:flex;flex-direction:column;align-items:center;gap:8px;}
  /* #print-root img{max-width:100%} above is an ID selector, which beats a
     plain class rule on specificity regardless of source order — repeat the
     #print-root prefix here so this override actually wins. */
  #print-root .qr-item img{height:88px;width:auto;max-width:none;border-radius:8px;}
  /* Source QR files are black modules on a white ground — on this dark CTA
     background that reads as a jarring white square. invert(1) turns them
     white-on-black, then screen blend drops the (now-black) ground out so
     only the white QR pattern shows, blending straight into the panel
     instead of sitting in a separate white box. */
  .qr-invert{filter:invert(1);mix-blend-mode:screen;}
  .qr-item span{font-size:11px;color:rgba(255,255,255,.55);white-space:nowrap;}
  .contact-details{display:flex;flex-direction:column;gap:10px;border-left:1px solid rgba(255,255,255,.15);padding-left:32px;}
  .contact-details .row{display:flex;align-items:center;gap:10px;color:rgba(255,255,255,.75);font-size:13px;}
  .contact-details .row svg{color:var(--gold-light);flex-shrink:0;}
  .page-footer{position:absolute;left:0;right:0;bottom:18px;z-index:5;display:flex;align-items:center;justify-content:space-between;padding:0 60px;}
  .page-footer .wordmark{color:var(--gold-light);font-size:11px;letter-spacing:.14em;font-weight:600;}
  .page-footer .fine{color:rgba(255,255,255,.4);font-size:8.5px;white-space:nowrap;text-align:right;}
`;
